import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { apiClient } from '../services/api.client';
import { queryKeys } from '../lib/queryKeys';
import { toast } from '../utils/toast';
import type { CaseDetail, UpdateCaseDto } from '@lnr-v2/types';

interface ValidationErrorDetail {
  path: string[];
  message: string;
}

interface ApiErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details?: ValidationErrorDetail[];
  };
}

interface UseUpdateCaseReturn {
  updateCase: (id: string, data: UpdateCaseDto) => Promise<CaseDetail>;
  loading: boolean;
  error: Error | null;
  reset: () => void;
}

interface UpdateCaseParams {
  id: string;
  data: UpdateCaseDto;
}

/**
 * Custom hook for updating cases with React Query
 *
 * Benefits:
 * - Automatic cache invalidation after successful update
 * - Consistent error handling
 * - Better integration with existing React Query setup
 * - Ensures cases list and detail views are refreshed after update
 * - Matches the pattern used in useCreateCase
 */
export const useUpdateCase = (): UseUpdateCaseReturn => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }: UpdateCaseParams): Promise<CaseDetail> => {
      const response = await apiClient.put(`cases/${id}`, {
        json: data,
      });

      const result = await response.json<{
        success: boolean;
        data: CaseDetail;
      }>();

      if (result.success && result.data) {
        return result.data;
      }
      throw new Error('Failed to update case');
    },
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: queryKeys.cases.detail(id) });

      // Snapshot the previous value
      const previousCase = queryClient.getQueryData<CaseDetail>(
        queryKeys.cases.detail(id)
      );

      // Optimistically update to the new value
      if (previousCase) {
        queryClient.setQueryData<CaseDetail>(queryKeys.cases.detail(id), {
          ...previousCase,
          ...data,
        });
      }

      // Return a context object with the snapshotted value
      return { previousCase };
    },
    onSuccess: (updatedCase, { id: originalId }) => {
      // Update the cache for ALL possible identifiers to ensure consistency:
      // 1. The original identifier (could be slug or CUID) used in the query
      // 2. The CUID from the response
      // 3. The new slug from the response (if it changed)

      // Update cache for the original identifier used
      queryClient.setQueryData(queryKeys.cases.detail(originalId), updatedCase);

      // Update cache for the CUID (if different from originalId)
      if (updatedCase.id !== originalId) {
        queryClient.setQueryData(
          queryKeys.cases.detail(updatedCase.id),
          updatedCase
        );
      }

      // Update cache for the new slug (if different from originalId and CUID)
      if (
        updatedCase.slug &&
        updatedCase.slug !== originalId &&
        updatedCase.slug !== updatedCase.id
      ) {
        queryClient.setQueryData(
          queryKeys.cases.detail(updatedCase.slug),
          updatedCase
        );
      }

      // Only invalidate LIST queries to refresh the cases list
      // Do NOT invalidate detail queries to prevent infinite refetch loops
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.cases.all, 'list'],
      });
    },
    onError: async (err: unknown, { id }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousCase) {
        queryClient.setQueryData(
          queryKeys.cases.detail(id),
          context.previousCase
        );
      }

      // Handle HTTP errors with detailed validation feedback
      if (err instanceof HTTPError) {
        const status = err.response.status;

        if (status === 422) {
          // Validation error - extract and display details
          try {
            const errorData = (await err.response.json()) as ApiErrorResponse;
            const details = errorData.error?.details;

            if (details && details.length > 0) {
              // Show first validation error message
              const firstError = details[0];
              toast.error(`Validation: ${firstError.message}`);
            } else {
              toast.error(errorData.error?.message || 'Validation failed');
            }
          } catch {
            toast.error('Validation error - please check your input');
          }
        } else if (status >= 500) {
          toast.error('Server error - please try again later');
        } else {
          toast.error('Failed to save changes');
        }
      } else {
        toast.error('Failed to update case');
      }
    },
  });

  // Convert mutation error to Error type for backwards compatibility
  const error = mutation.error ? (mutation.error as Error) : null;

  // Wrapper function to maintain backwards compatibility with existing API
  const updateCase = async (
    id: string,
    data: UpdateCaseDto
  ): Promise<CaseDetail> => {
    return mutation.mutateAsync({ id, data });
  };

  return {
    updateCase,
    loading: mutation.isPending,
    error,
    reset: mutation.reset,
  };
};
